import type {
  EditVehicleDocumentPromptResolve,
  VehicleDocumentNode,
} from '@/modules/vehicles'
import { EditVehicleDocumentModal } from '@/modules/vehicles/fleet'
import { useVehicleDocumentMutations } from '@/modules/vehicles/fleet/mutations.hooks'
import { getExpirationAndNotificationTime } from '@/modules/vehicles/list-filters'
import { useConfirm } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'

/** Add/edit/delete handlers for the vehicle documents page. */
export function useVehicleDocumentHandlers() {
  const translations = useTranslate({
    delete: 'documents.prompts.delete-document',
    deleteConfirmation: [
      'documents.prompts.delete-document-confirmation',
      { name: '' },
    ],
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()

  const { createDocument, patchDocument, deleteDocument } =
    useVehicleDocumentMutations()

  async function addDocument() {
    const { data } = await addPrompt<EditVehicleDocumentPromptResolve | null>(
      resolve => <EditVehicleDocumentModal askForVehicle onSubmit={resolve} />
    )
    if (!data?.form.file) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    await createDocument({
      variables: {
        input: {
          name: data.form.name,
          description: data.form.description,
          file: data.form.file,
          expirationDate,
          notificationTime,
          responsibleUser: data.hasNotification
            ? data.form.responsibleUser
            : null,
          // Asked for in the modal here: unlike the per-vehicle tab, this page
          // has no vehicle of its own to attach the document to.
          vehicle: data.form.vehicle ?? undefined,
        },
      },
    })
  }

  async function editDocument(document: VehicleDocumentNode) {
    const { data } = await addPrompt<EditVehicleDocumentPromptResolve | null>(
      resolve => (
        <EditVehicleDocumentModal
          vehicleDocument={document}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    await patchDocument({
      variables: {
        id: document.id,
        input: {
          name: data.form.name,
          description: data.form.description,
          expirationDate,
          notificationTime,
          responsibleUser: data.form.responsibleUser,
        },
      },
    })
  }

  async function removeDocument(document: VehicleDocumentNode) {
    const { data: answer } = await confirm(
      translations.deleteConfirmation({ name: document.name }),
      translations.delete
    )
    if (!answer) return

    await deleteDocument({ variables: { id: document.id } })
  }

  return { addDocument, editDocument, removeDocument }
}
